
import { computed, defineComponent, PropType, reactive } from 'vue'

// components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'

// types
import { CompanyT, CompanySetClientPayloadT } from '@/core/types/companies.types'

// realisations
import { companies } from '@/core/realisations/companies.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

export default defineComponent({
    components: {
        'the-button': TheButton,
        'button-dropdown': ButtonDropdown,
    },
    props: {
        item: {
            type: Object as PropType<CompanyT>,
            default: null,
        },
    },
    emits: [
        'update',
        'generate',
        'activate',
        'payers',
        'reset',
        'generate-transcript',
        'enable-verification',
        'verification-disabled',
        'sms-activated',
        'sms-disabled',
        'apply',
        'set-client',
        'billing',
        'refresh',
    ],
    setup(props, { emit }) {
        const { havePermissions, havePermission } = useAccount()

        const state = reactive({
            is_loading: false,
            is_sms_toggle_loading: false,
            is_dogovor_loading: false,
            is_1c_loading: false,
        })

        const activation_state = reactive({
            is_loading: false,
        })

        const mass_state = reactive({
            is_loading: false,
        })

        const notifications = useNotifications()

        const emitUpdate = () => {
            emit('update', props.item)
        }

        const emitPayers = () => {
            emit('payers', props.item?.id, props.item?.payer_companies)
        }

        const emitGenerate = () => {
            emit('generate')
        }

        const emitReset = () => {
            emit('reset', props.item.contact_email)
        }

        const emitGenerateTranscript = () => {
            emit('generate-transcript', props.item.id)
        }

        const emitSet = () => {
            const set_client_info: CompanySetClientPayloadT = {
                id: props.item.id,
                signerEmail: props.item.client_signer_email,
                approvingEmail: props.item.client_approving_email,
            }

            emit('set-client', set_client_info)
        }

        const activateCompany = async () => {
            const answer = confirm(
                `Вы действительно хотите активировать компанию '${props.item.name}'?`
            )
            if (!answer) return

            try {
                activation_state.is_loading = true

                await companies.activateCompany(props.item.id)
                notifications.notify({ title: 'Компания активирована', type: 'success' })

                emit('refresh')
            } catch (error) {
                notifications.notifyError({
                    title: 'При активации компании произошла ошибка',
                    error,
                })
            } finally {
                activation_state.is_loading = false
            }
        }

        const manageMassApprove = async () => {
            try {
                mass_state.is_loading = true

                if (props.item.mass_approve_enabled) {
                    await companies.disableMassApprove(props.item.id)
                    notifications.notify({
                        title: 'Выключено массовое закрытие ДК',
                        type: 'success',
                    })
                } else {
                    await companies.enableMassApprove(props.item.id)
                    notifications.notify({
                        title: 'Включено массовое закрытие ДК',
                        type: 'success',
                    })
                }

                emit('refresh')
            } catch (error) {
                notifications.notifyError({
                    title: 'Произошла ошибка',
                    error,
                })
            } finally {
                mass_state.is_loading = false
            }
        }

        const deactivateCompany = async () => {
            const answer = confirm(
                `Вы действительно хотите деактивировать компанию '${props.item.name}'?`
            )
            if (!answer) return

            try {
                activation_state.is_loading = true

                await companies.deactivateCompany(props.item.id)
                notifications.notify({ title: 'Компания деактивирована', type: 'success' })

                emit('refresh')
            } catch (error) {
                notifications.notifyError({
                    title: 'При деактивации компании произошла ошибка',
                    error,
                })
            } finally {
                activation_state.is_loading = false
            }
        }

        const enableVerification = async () => {
            emit('enable-verification', { ...props.item })
        }

        const disableVerification = async (id: number) => {
            await companies.disableVerification(id)
        }

        const handleVerification = async () => {
            try {
                state.is_loading = true

                if (props.item.verify) {
                    const answer = confirm(
                        `Вы действительно хотите отключить верификацию для компании ${props.item.name}`
                    )
                    if (!answer) return

                    await disableVerification(props.item.id)
                    emit('verification-disabled', props.item.id)

                    notifications.notify({
                        title: 'Верификация выключена',
                        type: 'success',
                    })
                } else {
                    enableVerification()
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Ошибка отключения верификации',
                })
            } finally {
                state.is_loading = false
            }
        }

        const canToggleSmsSetting = computed(() => {
            if (!havePermissions(['company-company-settings-update', 'all-root-permission']))
                return false

            if (!props.item || !props.item.companySettings || !props.item.companySettings.length) {
                return false
            }

            const index = props.item.companySettings.findIndex((setting) => setting.key === 'sms')

            if (index < 0) return false
            return true
        })

        const isSmsSettingActivated = computed(() => {
            if (!canToggleSmsSetting.value) return false

            const index = props.item.companySettings.findIndex((setting) => setting.key === 'sms')
            if (index < 0) return false

            return props.item.companySettings[index].isActive
        })

        const toggleSmsSetting = async () => {
            let action = 'включить'

            if (isSmsSettingActivated.value) {
                action = 'выключить'
            }

            const answer = confirm(
                `Вы действительно хотите ${action} СМС для компании ${props.item.name}?`
            )
            if (!answer) return

            const setting = props.item.companySettings.find((setting) => setting.key === 'sms')
            if (!setting) return

            try {
                state.is_sms_toggle_loading = true

                if (!isSmsSettingActivated.value) {
                    await companies.activateCompanySetting(props.item.id, setting.settingId)

                    emit('sms-activated', props.item.id)

                    notifications.notify({
                        title: 'СМС включены',
                        description: `СМС для компании ${props.item.name} включены`,
                        type: 'success',
                    })
                } else {
                    await companies.disableCompanySetting(props.item.id, setting.settingId)

                    emit('sms-disabled', props.item.id)

                    notifications.notify({
                        title: 'СМС выключены',
                        description: `СМС для компании ${props.item.name} выключены`,
                        type: 'success',
                    })
                }
            } catch (error) {
                console.error(error)

                let action = 'включении'

                if (isSmsSettingActivated.value) {
                    action = 'выключении'
                }

                notifications.notifyError({
                    title: `При ${action} СМС для компании ${props.item.name} произошла ошибка`,
                    error,
                })
            } finally {
                state.is_sms_toggle_loading = false
            }
        }

        const toggleBilling = () => {
            emit('billing')
        }

        const emitApply = () => {
            emit('apply', props.item.id)
        }

        const resendContract = async () => {
            if (state.is_dogovor_loading) return

            const answer = confirm('Вы уверены что хотите переотправить договор?')
            if (!answer) return

            try {
                state.is_dogovor_loading = true

                await companies.resendCompanyContract(props.item.id)
                notifications.notify({
                    title: 'Переотправлен',
                    description: 'Договор компании переотправлен',
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При переотправке договора произошла ошибка',
                })
            } finally {
                state.is_dogovor_loading = false
            }
        }

        const resendTo1C = async () => {
            if (state.is_1c_loading) return

            const answer = confirm('Вы уверены что хотите переотправить компанию 1С?')
            if (!answer) return

            try {
                state.is_1c_loading = true

                await companies.resendCompanyTo1C(props.item.id)
                notifications.notify({ title: 'Компания переотправлена', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При переотправке компании произошла ошибка',
                })
            } finally {
                state.is_1c_loading = false
            }
        }

        const isCanResendContract = computed(() => {
            if (!props.item.is_standard_contract) return false
            if (havePermissions(['company-resend-contract', 'all-root-permission'])) return true
            return false
        })

        const isCanResendTo1C = computed(() => {
            if (props.item.code_1c) return false
            if (havePermissions(['company-resend-one-c', 'all-root-permission'])) return true

            return false
        })

        const massApproveText = computed(() => {
            return props.item.mass_approve_enabled
                ? 'Выключить массовое закрытие ДК'
                : 'Включить массовое закрытие ДК'
        })

        return {
            state,
            mass_state,
            emitUpdate,
            emitPayers,
            emitGenerate,
            emitReset,
            emitGenerateTranscript,
            handleVerification,
            enableVerification,
            disableVerification,
            activation_state,
            activateCompany,
            deactivateCompany,
            canToggleSmsSetting,
            isSmsSettingActivated,
            toggleSmsSetting,
            emitApply,
            emitSet,
            resendContract,
            resendTo1C,
            toggleBilling,
            isCanResendContract,
            isCanResendTo1C,
            havePermission,
            havePermissions,
            massApproveText,
            manageMassApprove,
        }
    },
})
